import {
  CTABanner as CTABannerUI,
  CTALink,
  CTABannerProps as CTABannerPropsUI
} from "@business-finland/wif-ui-lib/dist/CTABanner";
import React from "react";

import useImageDataHandler from "../../../hooks/useImageDataHandler";
import { resolveLinkSwitchableField } from "../../../util/magnolia";
import { LinkSwitchableField } from "../../../types/wif-next";
import useEmployerSite from "../../../contexts/EmployerSiteContext";
import { resolveCmsBackgroundColor } from "../../../util/cms-background-color";

export type CTABannerType = {
  link?: LinkSwitchableField;
  btnText: string;
} & (
  | {
      field: "buttonOnly";
      alignToArticleSection?: boolean;
      backgroundColor?: "white" | "color";
    }
  | {
      field: "default";
      headline: string;
      bodyText?: string;
    }
  | {
      field: "imageBackground";
      headline: string;
      bodyText?: string;
      image: Wif.Mgnl.Image;
      imageCredit?: string;
    }
);

export type CTABannerProps = {
  ctaBannerType: CTABannerType;
};

const transformLink = (ctaBannerType: CTABannerType): CTALink => {
  const resolvedLink = resolveLinkSwitchableField(ctaBannerType.link, false);
  return {
    title: ctaBannerType.btnText,
    href: resolvedLink.linkHref,
    isExternalLink: resolvedLink.isExternalLink,
  };
};

export function CTABanner({ ctaBannerType }: CTABannerProps): React.JSX.Element {
  const imageDataHandler = useImageDataHandler();
  const { isEmployerSite } = useEmployerSite();

  const alignToArticleSection =
    ctaBannerType.field === "buttonOnly" &&
    Boolean(ctaBannerType.alignToArticleSection);
  const buttonOnlyBackgroundColor =
    ctaBannerType.field === "buttonOnly"
      ? resolveCmsBackgroundColor(ctaBannerType.backgroundColor, isEmployerSite)
      : undefined;

  const props: CTABannerPropsUI = {
    link: transformLink(ctaBannerType),
    btnText: ctaBannerType.btnText,
    alignToArticleSection,
    backgroundColor: buttonOnlyBackgroundColor,
  };

  if (
    ctaBannerType.field === "default" ||
    ctaBannerType.field === "imageBackground"
  ) {
    props.headline = ctaBannerType.headline;
    props.body = ctaBannerType.bodyText;
  }

  if (ctaBannerType.field === "imageBackground") {
    const imageData = imageDataHandler(ctaBannerType.image);

    props.image = {
      credits: ctaBannerType.imageCredit || ctaBannerType.image.metadata.rights,
      altText: "",
      ...imageData,
    };
  }

  return <CTABannerUI {...props} />;
}
